import { RESET_SELECTION } from "~/features/configurator/common/advancedTranslationUtils";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import * as UnitCstics from "~/features/configurator/Linear/v2/configuration/kuem/KUEMUnitCstics";
import type { ConfiguratorDefinition } from "~/features/configurator/common/v2/StepDefinition";
import { URL_CONTACT_US } from "~/utils/url/staticUrls";
import { csticHasValue, getCsticNumericValue, getCsticsValue } from "~/features/configurator/common/v2/helper";
import {
  ExtraConfig,
  HelperCstics,
  InputCstics,
  type AlarHelperTextFactoryParams,
} from "~/features/configurator/Rail/components/ConfigureRailSubstep/constants";
import { userIsCustomer } from "~/domains/user/utils/roles";

export const KUEMUnitConfiguratorDefinition: ConfiguratorDefinition = {
  description: [
    {
      condition: ({ mainKmat }) => {
        const [guidewayDesign] = getCsticsValue(mainKmat, UnitCstics.GuidewayDesign);
        return guidewayDesign === "/";
      },
      value: "configurator.unit.description./",
    },
    {
      condition: ({ mainKmat }) => {
        const [guidewayDesign] = getCsticsValue(mainKmat, UnitCstics.GuidewayDesign);
        return guidewayDesign === "W";
      },
      value: "configurator.unit.description.W",
    },
  ],
  mediaUrl: [
    {
      condition: ({ mainKmat }) => {
        const [guidewayAmount, carriageAmount] = getCsticsValue(
          mainKmat,
          UnitCstics.GuidewayAmount,
          UnitCstics.CarriageAmount,
        );
        return guidewayAmount === "1.0" && carriageAmount === "1.0";
      },
      value: "configurator.unit.cstic.systemLayout.tile.1.1.imageUrl",
    },
    {
      condition: ({ mainKmat }) => {
        const [guidewayAmount, carriageAmount] = getCsticsValue(
          mainKmat,
          UnitCstics.GuidewayAmount,
          UnitCstics.CarriageAmount,
        );
        return guidewayAmount === "1.0" && carriageAmount === "2.0";
      },
      value: "configurator.unit.cstic.systemLayout.tile.1.2.imageUrl",
    },
    {
      condition: ({ mainKmat }) => {
        const [guidewayAmount, carriageAmount] = getCsticsValue(
          mainKmat,
          UnitCstics.GuidewayAmount,
          UnitCstics.CarriageAmount,
        );
        return guidewayAmount === "1.0" && carriageAmount === "3.0";
      },
      value: "configurator.unit.cstic.systemLayout.tile.1.3.imageUrl",
    },
  ],
  pages: [
    {
      name: "configurator.unit.page.1.name",
      enabled: true,
      resetLabel: RESET_SELECTION,
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.SystemLayout,
          name: "configurator.unit.cstic.systemLayout.name",
          description: "configurator.unit.cstic.systemLayout.description",
          info: "configurator.unit.cstic.systemLayout.info",
          tiles: [
            {
              name: "configurator.unit.cstic.systemLayout.tile.1.1.name",
              imageUrl: "configurator.unit.cstic.systemLayout.tile.1.1.imageUrl",
              cstics: [
                {
                  csticName: UnitCstics.GuidewayAmount,
                  value: "1.0",
                },
                {
                  csticName: UnitCstics.CarriageAmount,
                  value: "1.0",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.systemLayout.tile.1.2.name",
              imageUrl: "configurator.unit.cstic.systemLayout.tile.1.2.imageUrl",
              cstics: [
                {
                  csticName: UnitCstics.GuidewayAmount,
                  value: "1.0",
                },
                {
                  csticName: UnitCstics.CarriageAmount,
                  value: "2.0",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.systemLayout.tile.1.3.name",
              imageUrl: "configurator.unit.cstic.systemLayout.tile.1.3.imageUrl",
              cstics: [
                {
                  csticName: UnitCstics.GuidewayAmount,
                  value: "1.0",
                },
                {
                  csticName: UnitCstics.CarriageAmount,
                  value: "3.0",
                },
              ],
            },
          ],
          tilesPerRow: 3,

          csticsToReset: [UnitCstics.GuidewayAmount, UnitCstics.CarriageAmount],

          visible: true,

          extraTexts: {
            helpTileName: "configurator.unit.cstic.systemLayout.helpTile.name",
            helpTileUrl: URL_CONTACT_US,
            helpTileImageUrl: "https://cdn.schaeffler-ecommerce.com/frontend/linear/linear_1_1_contact.png",
          },
        },
        {
          type: SubstepType.ReferenceCarriage,
          visible: true,
          name: "configurator.unit.step.referenceCarriage.name",
          description: "configurator.unit.step.referenceCarriage.description",
          info: "configurator.unit.cstic.referenceCarriage.info",

          groupedTiles: [
            {
              tiles: [
                {
                  name: "configurator.unit.cstic.carriageType./.name",
                  imageUrl: "configurator.unit.cstic.carriageType./.image",
                  cstics: [
                    {
                      csticName: UnitCstics.GuidewayDesign,
                      value: "/",
                    },
                    {
                      csticName: UnitCstics.CarriageDesign,
                      value: "/",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.unit.cstic.carriageType.L.name",
                  imageUrl: "configurator.unit.cstic.carriageType.L.image",
                  cstics: [
                    {
                      csticName: UnitCstics.GuidewayDesign,
                      value: "/",
                    },
                    {
                      csticName: UnitCstics.CarriageDesign,
                      value: "L",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.unit.cstic.carriageType.W.name",
                  imageUrl: "configurator.unit.cstic.carriageType.W.image",
                  cstics: [
                    {
                      csticName: UnitCstics.GuidewayDesign,
                      value: "W",
                    },
                    {
                      csticName: UnitCstics.CarriageDesign,
                      value: "/",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.unit.cstic.carriageType.WL.name",
                  imageUrl: "configurator.unit.cstic.carriageType.WL.image",
                  cstics: [
                    {
                      csticName: UnitCstics.GuidewayDesign,
                      value: "W",
                    },
                    {
                      csticName: UnitCstics.CarriageDesign,
                      value: "L",
                    },
                  ],
                  visible: true,
                },
              ],
              visible: true,
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [UnitCstics.GuidewayDesign, UnitCstics.CarriageDesign],
        },
        {
          type: SubstepType.Size,
          name: "configurator.unit.cstic.size.name",
          tiles: [
            {
              name: "configurator.unit.cstic.sizeCode.07.name",
              cstics: [
                {
                  csticName: UnitCstics.SizeCode,
                  value: "07",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.sizeCode.09.name",
              cstics: [
                {
                  csticName: UnitCstics.SizeCode,
                  value: "09",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.sizeCode.12.name",
              cstics: [
                {
                  csticName: UnitCstics.SizeCode,
                  value: "12",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.sizeCode.15.name",
              cstics: [
                {
                  csticName: UnitCstics.SizeCode,
                  value: "15",
                },
              ],
            },
          ],
          tilesPerRow: 12,
          csticsToReset: [UnitCstics.SizeCode],
          visible: true,
        },
        {
          type: SubstepType.Preload,
          name: "configurator.unit.cstic.preload.name",
          info: "configurator.unit.cstic.preload.info",
          tiles: [
            {
              name: "configurator.unit.cstic.preload.V0.name",
              cstics: [
                {
                  csticName: UnitCstics.Preload,
                  value: "V0",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.preload.V1.name",
              cstics: [
                {
                  csticName: UnitCstics.Preload,
                  value: "V1",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.preload.V2.name",
              cstics: [
                {
                  csticName: UnitCstics.Preload,
                  value: "V2",
                },
              ],
            },
          ],
          tilesPerRow: 6,
          csticsToReset: [UnitCstics.Preload],
          visible: true,
        },
        {
          type: SubstepType.Accuracy,
          name: "configurator.unit.cstic.accuracy.name",
          info: "configurator.unit.cstic.accuracy.info",
          visible: true,
          csticsToReset: [UnitCstics.Accuracy],
          tiles: [
            {
              name: "configurator.unit.cstic.accuracy.G1.name",
              cstics: [
                {
                  csticName: UnitCstics.Accuracy,
                  value: "G1",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.accuracy.G2.name",
              cstics: [
                {
                  csticName: UnitCstics.Accuracy,
                  value: "G2",
                },
              ],
            },
          ],
          tilesPerRow: 6,
        },
        {
          type: SubstepType.Sealing,
          name: "configurator.unit.cstic.sealing.name",
          info: "configurator.unit.cstic.sealing.info",
          tiles: [
            {
              name: "configurator.unit.cstic.sealing./.name",
              cstics: [
                {
                  csticName: UnitCstics.Sealing,
                  value: "/",
                },
              ],
            },
            {
              name: "configurator.unit.cstic.sealing.PP.name",
              cstics: [
                {
                  csticName: UnitCstics.Sealing,
                  value: "PP",
                },
              ],
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [UnitCstics.Sealing],

          visible: true,
        },
        {
          type: SubstepType.KLLTGuidewaySet,
          visible: [
            {
              value: true,
              condition: ({ mainKmat }) => {
                return [
                  csticHasValue(mainKmat, UnitCstics.GuidewayAmount),
                  csticHasValue(mainKmat, UnitCstics.CarriageAmount),
                  csticHasValue(mainKmat, UnitCstics.GuidewayDesign),
                  csticHasValue(mainKmat, UnitCstics.SizeCode),
                  csticHasValue(mainKmat, UnitCstics.Preload),
                  csticHasValue(mainKmat, UnitCstics.Accuracy),
                  csticHasValue(mainKmat, UnitCstics.Sealing),
                ].every(Boolean);
              },
            },
          ],

          name: "configurator.unit.cstic.guidewaySet.name",

          extraTexts: {
            lowerMessage: "configurator.unit.cstic.guidewaySet.infoMessage.upper", // On purpose
            lengthName: "configurator.unit.cstic.guidewayLength.name", // TODO - Move to cstic
            lengthInfo: "configurator.unit.cstic.guidewayLength.info", // TODO - Move to cstic
            alarName: "configurator.unit.section.alar.name",
            alarInfo: "configurator.unit.section.alar.info",
            alarRecommended: "configurator.unit.section.alar.recommended",
            alarWarningMessage: "configurator.unit.section.alar.warning",
            partsSectionName: "configurator.unit.section.parts.name",
            partsSectionInfo: "configurator.unit.section.parts.info",
          },

          inputCstics: {
            [InputCstics.TotalLength]: {
              name: UnitCstics.GuidewayLength,

              required: true,
            },
            [InputCstics.aL]: {
              name: UnitCstics.AL,

              required: true,
              visible: [
                {
                  condition: ({ mainKmat, currentKmat }) => {
                    return getCsticNumericValue(currentKmat, UnitCstics.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.aR]: {
              name: UnitCstics.AR,

              visible: [
                {
                  condition: ({ mainKmat, currentKmat }) => {
                    return getCsticNumericValue(currentKmat, UnitCstics.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.partSizingType]: {
              name: UnitCstics.PartSizing,
              disabled: true,

              visible: [
                {
                  condition: ({ currentKmat }) => {
                    return getCsticNumericValue(currentKmat, UnitCstics.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.partAmount]: {
              name: UnitCstics.PartAmount,

              disabled: true,
              visible: [
                {
                  condition: ({ currentKmat }) => {
                    return getCsticNumericValue(currentKmat, UnitCstics.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
          },

          helperCstics: {
            [HelperCstics.totalLengthMin]: {
              name: UnitCstics.GuidewayLengthMin,
            },
            [HelperCstics.totalLengthMax]: {
              name: UnitCstics.GuidewayLengthMax,
            },
            [HelperCstics.alHardMax]: {
              name: UnitCstics.AlarHardMax,
            },
            [HelperCstics.arHardMax]: {
              name: UnitCstics.AlarHardMax,
            },
            [HelperCstics.alRecommendedMin]: {
              name: UnitCstics.AlMinRecommended,
            },
            [HelperCstics.alRecommendedMax]: {
              name: UnitCstics.AlMaxRecommended,
            },
            [HelperCstics.arRecommendedMin]: {
              name: UnitCstics.ArMinRecommended,
            },
            [HelperCstics.arRecommendedMax]: {
              name: UnitCstics.ArMaxRecommended,
            },
          },

          extraConfig: {
            [ExtraConfig.AlarHelperTextFactory]: ({
              hardMin = 0,
              hardMax = 0,
              recommendedMin = 0,
              recommendedMax = 0,
              t,
              value,
            }: AlarHelperTextFactoryParams) => {
              if (value === null) {
                return "";
              }
              if (value <= hardMin) {
                return "<=0mm";
              }
              if (value >= hardMax) {
                return `>=${hardMax}mm`;
              }

              if (value < recommendedMin || value > recommendedMax) {
                return `${t("common.min")} ${recommendedMin} / ${t("common.recommended")} ${recommendedMax}`;
              }

              return "";
            },
            [ExtraConfig.showNotRecommendedWarning]: true,
          },

          csticsToReset: [UnitCstics.GuidewayLength, UnitCstics.AL],
        },
      ],
    },
    {
      name: "configurator.unit.page.4.name",
      enabled: true,
      resetLabel: "",
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.SummaryPage,
          visible: true,

          info: [
            {
              condition: () => true,
              value: ({ mainKmat, subKmats }) => {
                const userPart = userIsCustomer() ? "business" : "plus";
                return `configurator.unit.summary.info.above.${userPart}`;
              },
            },
          ],
        },
      ],
    },
  ],
};
